import React, { useEffect, useState, useRef } from "react";
import { Formik, Form } from "formik";
import {
  useUpdateProspectCompanyInfoMutation,
  useUpdateBaseProspectMutation,
  useUpdateRelatedEntitiesMutation,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import { businessDetailsValidationSchema } from "../../utils/ValidationSchema";
import BusinessDetailsComponent from "./components/BusinessDetailsComponent";
import { BusinessDetailsFormType } from "../../utils/formType";
import { BusinessDetailsInitialFormValues } from "../../utils/initialFormValues";
import { BusinessDetailsType } from "types";
import FooterButton from "uicomponents/FooterButton";
import Alert from "uicomponents/Alert";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  LOADING_BUTTON_TEXT,
} from "pages/WorkersCompApplcation/utils/contants";
import { convertStringIntoBool, errorMessageMap } from "utils/helper";
import {
  trackFormError,
  trackPageViewUserStep,
  trackCoreAppFormError,
} from "utils/analytics/trackEvent";
import { Alert as AlertNotice } from "@justworkshr/milo-core";
import { SystemIcon } from "@justworkshr/milo-components";
import { getNeonRedirectUrl } from "utils/helper";
import { hasQueryParam } from "utils/helper";
import styles from "./BusinessDetails.module.css";

interface IBusinessDetailsProps {
  setNextStep: () => void;
  data: BusinessDetailsType;
  setPreviousStep: () => void;
}

const BusinessDetails = ({ ...props }: IBusinessDetailsProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(1, "business details");
  }, []);

  const wc_flow_with_core_app = hasQueryParam("usecoreapp");

  const [updateProspectCompanyInfo, { loading: updateCompanyInfoLoading }] =
    useUpdateProspectCompanyInfoMutation();

  const [updateBaseProspect, { loading: updateBaseProspectLoading }] =
    useUpdateBaseProspectMutation();

  const [updateRelatedEntities, { loading: updateRelatedEntitiesLoading }] =
    useUpdateRelatedEntitiesMutation();

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoadingSteps, setIsLoadingSteps] = useState(false);

  const [showCoreAppFieldsError, setShowCoreAppFieldsError] = useState(false);

  const { setNextStep, data, setPreviousStep } = props;

  const initialFormValues: BusinessDetailsFormType =
    BusinessDetailsInitialFormValues(data);

  const handleOnSubmit = async (values: BusinessDetailsFormType) => {
    const currentYear = new Date().getFullYear();
    setIsLoadingSteps(true);
    try {
      const prospectUuid = getProspectUuid();
      await updateBaseProspect({
        variables: {
          uuid: prospectUuid,
          dba: values.dba,
          ein: values.ein,
          website: data?.website,
          companyName: values.companyName,
          yearFounded: Number(values.yearFounded),
        },
      });
      await updateProspectCompanyInfo({
        variables: {
          uuid: prospectUuid,
          hoursOfOperation: values.hoursOfOperation,
          ownerHalfStakeOther: convertStringIntoBool(
            values.ownerHalfStakeOther
          ),
          yearsInBusiness: currentYear - Number(values.yearFounded),
          entityType: values.entityType,
          isNonProfit: convertStringIntoBool(values.isNonProfit),
          industry: values.industry,
          industryNaics4: values.industryNaics4,
          industryNaics6: values.industryNaics6,
          industryOther: values.industryOther,
        },
      });
      if (values.ownerHalfStakeOther === "Yes") {
        await updateRelatedEntities({
          variables: {
            uuid: prospectUuid,
            relatedEntities: values.relatedEntities.map((entity) => ({
              uuid: entity.uuid || "",
              legalName: entity.reName,
              description: entity.reDescription,
              isDependent: convertStringIntoBool(entity.reIsDependent) || false,
              dba: entity.reDba,
              ein: entity.reEin,
              hasW2Employees:
                convertStringIntoBool(entity.reHasW2Employees) || false,
              hasOverlappingEmployees:
                convertStringIntoBool(entity.reHasOverlappingEmployees) ||
                false,
              sharesLocation:
                convertStringIntoBool(entity.reSharesLocation) || false,
            })),
          },
        });
      }
      setNextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleBack = (): void => {
    setPreviousStep();
  };

  const fieldRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Helper function that makes the browser scroll a specific field into view
  const focusField = (fieldName: string) => {
    if (fieldRefs.current[fieldName]) {
      fieldRefs.current[fieldName]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialFormValues}
        validationSchema={businessDetailsValidationSchema}
        onSubmit={handleOnSubmit}
      >
        {({ errors, handleSubmit }) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                const currentValidationErrors = Object.keys(errors);
                let firstErrorFieldName = currentValidationErrors[0];

                // Handle missing fields from potential failed core app submission
                if (wc_flow_with_core_app) {
                  const coreAppRequiredFields = [
                    "companyName",
                    "yearFounded",
                    "entityType",
                    "industry",
                  ];
                  const missingCoreAppFields = coreAppRequiredFields.filter(
                    (field) => currentValidationErrors.includes(field)
                  );
                  const hasMissingCoreAppFields =
                    missingCoreAppFields.length > 0;
                  setShowCoreAppFieldsError(hasMissingCoreAppFields);
                  if (hasMissingCoreAppFields) {
                    trackCoreAppFormError(missingCoreAppFields);
                  }
                }

                // Handle relatedEntities validation errors
                if (
                  firstErrorFieldName === "relatedEntities" &&
                  errors.relatedEntities &&
                  errors.relatedEntities.length > 0
                ) {
                  const relatedEntitiesErrors = errors.relatedEntities?.[0];
                  const firstRelatedEntitiesError = Object.keys(
                    relatedEntitiesErrors
                  )[0];
                  focusField(`relatedEntities[0].${firstRelatedEntitiesError}`);
                } else {
                  focusField(firstErrorFieldName);
                }

                handleSubmit(e);
              }}
            >
              <BusinessDetailsComponent data={data} fieldRefs={fieldRefs} />
              {showCoreAppFieldsError && (
                <AlertNotice
                  color="destructive"
                  className={styles.prefilledAlert}
                >
                  <div className={styles.alertContent}>
                    <SystemIcon iconName="error" />
                    <p>
                      Some required details from the previous step are missing.
                      Please{" "}
                      <a
                        href={`${getNeonRedirectUrl(
                          "/enrollment-hub/applications/core-app/company-form"
                        )}`}
                      >
                        {" "}
                        complete your information{" "}
                      </a>{" "}
                      in order to continue.
                    </p>
                  </div>
                </AlertNotice>
              )}
              <FooterButton
                nextButtonText={
                  updateCompanyInfoLoading ||
                  updateBaseProspectLoading ||
                  isLoadingSteps
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={
                  updateCompanyInfoLoading ||
                  updateBaseProspectLoading ||
                  updateRelatedEntitiesLoading ||
                  isLoadingSteps
                }
                handleBackClick={handleBack}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default BusinessDetails;
